import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"
import type { CartItem } from "./model"
import { ProductId } from "./model"
import { injectSlice } from "@/base"

export type State = {
  base: ProductId
  addons: CartItem[]
  interval: "month" | "year"
  isDirty: boolean
}

const initialState: State = {
  base: ProductId.Trickster,
  addons: [],
  interval: "month",
  isDirty: false,
}

const slice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    init(state, { payload }: PayloadAction<Omit<State, "isDirty">>) {
      state.base = payload.base
      state.addons = payload.addons
      state.interval = payload.interval
      state.isDirty = false
    },
    setBase(
      state,
      { payload }: PayloadAction<{ id: ProductId; allowedAddons: ProductId[] }>,
    ) {
      state.base = payload.id
      state.addons = state.addons.filter(({ productId }) =>
        payload.allowedAddons.includes(productId),
      )
      state.isDirty = true
    },
    addAddon(state, { payload }: PayloadAction<CartItem>) {
      state.addons = [
        ...state.addons.filter((item) => item.productId !== payload.productId),
        payload,
      ]
      state.isDirty = true
    },
    removeAddon(state, { payload }: PayloadAction<ProductId>) {
      state.addons = state.addons.filter((item) => item.productId !== payload)
      state.isDirty = true
    },
  },
  selectors: {
    getQuantity: (state, productId: ProductId) =>
      state.base === productId
        ? 1
        : (state.addons.find((item) => item.productId === productId)
            ?.quantity ?? 0),
    getBase: (state) => state.base,
    getCart: (state) => ({
      items: [{ productId: state.base, quantity: 1 }, ...state.addons],
      interval: state.interval,
    }),
    getIsDirty: (state) => state.isDirty,
  },
})

export const useSelector = injectSlice(slice)

export const { init, setBase, addAddon, removeAddon } = slice.actions
export const { getQuantity, getBase, getCart, getIsDirty } = slice.selectors

export default slice
